// src/context/MultiCartContext.js — FIXED (adds removeItem per vendor)
import React, { createContext, useContext, useReducer, useMemo } from 'react';

const MultiCartContext = createContext(null);
const TAX = 0.05;

function reducer(state, action) {
  switch (action.type) {

    case 'ADD_ITEM': {
      const { vendorId, item } = action;
      const vendorCart = state[vendorId] || [];
      const existing   = vendorCart.find((i) => i.id === item.id);
      return {
        ...state,
        [vendorId]: existing
          ? vendorCart.map((i) => i.id === item.id ? { ...i, qty: i.qty + 1 } : i)
          : [...vendorCart, { ...item, qty: 1 }],
      };
    }

    case 'REMOVE_ITEM': {
      const { vendorId, itemId } = action;
      const vendorCart = state[vendorId] || [];
      const updated    = vendorCart.filter((i) => i.id !== itemId);
      if (!updated.length) {
        const { [vendorId]: _, ...rest } = state;
        return rest;
      }
      return { ...state, [vendorId]: updated };
    }

    case 'SET_QTY': {
      const { vendorId, itemId, qty } = action;
      const vendorCart = state[vendorId] || [];
      if (qty <= 0) {
        const updated = vendorCart.filter((i) => i.id !== itemId);
        if (!updated.length) {
          const { [vendorId]: _, ...rest } = state;
          return rest;
        }
        return { ...state, [vendorId]: updated };
      }
      return {
        ...state,
        [vendorId]: vendorCart.map((i) => i.id === itemId ? { ...i, qty } : i),
      };
    }

    case 'REMOVE_VENDOR': {
      const { [action.vendorId]: _, ...rest } = state;
      return rest;
    }

    case 'CLEAR':
      return {};

    default:
      return state;
  }
}

export function MultiCartProvider({ children }) {
  const [carts, dispatch] = useReducer(reducer, {});

  const totals = useMemo(() => {
    const byVendor = {};
    let grandSubtotal = 0;

    for (const [vendorId, items] of Object.entries(carts)) {
      const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
      const tax      = parseFloat((subtotal * TAX).toFixed(2));
      const total    = parseFloat((subtotal + tax).toFixed(2));
      byVendor[vendorId] = { items, subtotal, tax, total };
      grandSubtotal += total;
    }

    return {
      byVendor,
      grandTotal:  parseFloat(grandSubtotal.toFixed(2)),
      vendorCount: Object.keys(carts).length,
      totalItems:  Object.values(carts).flat().reduce((s, i) => s + i.qty, 0),
    };
  }, [carts]);

  const getCheckoutPayload = () => {
    const payload = {};
    for (const [vendorId, items] of Object.entries(carts)) {
      payload[vendorId] = items.map((i) => ({
        menuItemId:       i.id,
        quantity:         i.qty,
        modifierNote:     i.modifierNote || '',
        bundleComponents: i.bundleComponentsDetail || undefined,
      }));
    }
    return payload;
  };

  return (
    <MultiCartContext.Provider value={{
      carts,
      totals,
      addItem:      (vendorId, item)         => dispatch({ type: 'ADD_ITEM',      vendorId, item }),
      removeItem:   (vendorId, itemId)       => dispatch({ type: 'REMOVE_ITEM',   vendorId, itemId }),
      setQty:       (vendorId, itemId, qty)  => dispatch({ type: 'SET_QTY',       vendorId, itemId, qty }),
      removeVendor: (vendorId)               => dispatch({ type: 'REMOVE_VENDOR', vendorId }),
      clearCart:    ()                       => dispatch({ type: 'CLEAR' }),
      getCheckoutPayload,
    }}>
      {children}
    </MultiCartContext.Provider>
  );
}

export const useMultiCart = () => useContext(MultiCartContext);
